import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";

// Tweens a number from its previous value to the new one and flashes the card when it changes
export function useAnimatedNumber(value, duration = 700) {
  const [display, setDisplay] = useState(value);
  const [pulse, setPulse] = useState(null); // "up" | "down" | null
  const prevRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = Number(value) || 0;
    if (from === to) return;
    setPulse(to > from ? "up" : "down");
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else { prevRef.current = to; setTimeout(() => setPulse(null), 600); }
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return [display, pulse];
}

export function StatCard({ icon: Icon, iconClass, label, value, testId }) {
  const [display, pulse] = useAnimatedNumber(value);
  const ring = pulse === "up" ? "ring-1 ring-green-500/50 bg-green-500/10" : pulse === "down" ? "ring-1 ring-amber-500/50 bg-amber-500/10" : "bg-card/50";
  return (
    <Card className={`border-border transition-all duration-500 ${ring}`} data-testid={testId} data-pulse={pulse || ""}>
      <CardContent className="p-2.5">
        <div className="flex items-center justify-between">
          <Icon className={`h-4 w-4 ${iconClass} transition-transform duration-300 ${pulse ? "scale-125" : ""}`} />
          <span className={`text-lg font-bold tabular-nums transition-transform duration-300 ${pulse ? "scale-110" : ""}`} data-testid={`${testId}-value`}>{display}</span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}
